import { Suspense, useCallback, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import { useAccountPermissions } from '@frameio/permissions';
import {
  ContextMenuContextProvider,
  useActiveAccountId,
} from '@frameio/shared/contexts';
import { getLink } from '@frameio/shared/routing';
import { isUUID } from '@frameio/utils/formatters';
import { List, ListItem } from '@frameio/vapor';
import { usePaginatedWorkspaces } from '../../../shared';
import {
  ContextMenu,
  Header,
  Workspace,
  WorkspaceItem,
  WorkspaceItemContainer,
} from './components';
import TEXT from './generated/TEXT/Workspaces';

type Props = {
  onItemSelected?: () => void;
};

export const Workspaces = ({ onItemSelected }: Props) => {
  const router = useRouter();
  const pathname = usePathname();
  const accountId = useActiveAccountId();
  const canCreateWorkspace = useAccountPermissions({
    accountId,
    check: 'canCreateWorkspace',
  });
  const isGlobalNavigationFlagEnabled = useFeatureFlagEnabled(
    'v4.global_navigation'
  );

  const renderListItem = useCallback(
    ({ id }: { id: string }) => {
      return isGlobalNavigationFlagEnabled ? (
        <ListItem key={id} as={WorkspaceItemContainer}>
          <Suspense fallback={<div>...</div>}>
            <WorkspaceItem workspaceId={id} />
          </Suspense>
        </ListItem>
      ) : (
        <ListItem key={id}>
          <Suspense fallback={<div>...</div>}>
            <Workspace workspaceId={id} />
          </Suspense>
        </ListItem>
      );
    },
    [isGlobalNavigationFlagEnabled]
  );

  const navigateToWorkspace = useCallback(
    (id: string) => {
      router.push(getLink({ page: 'workspaces', workspaceId: id }));
    },
    [router]
  );

  const selectedWorkspace = useMemo(() => {
    const fragments = pathname.split('/');
    if (fragments[1] === 'workspaces' && isUUID(fragments[2])) {
      return [fragments[2]];
    }
    return [];
  }, [pathname]);

  const { items, loading, fetchNext } = usePaginatedWorkspaces();
  const isLoading = loading && items.length === 0;
  const isHidden = items.length === 0 && !canCreateWorkspace;

  if (isLoading || isHidden) {
    return null;
  }

  return (
    <>
      <Header />
      <ContextMenuContextProvider>
        <List
          animate={false}
          aria-label={TEXT.workspacesListAriaLabel}
          items={items}
          linkBehavior="action"
          onBoundary={(boundary) => {
            if (boundary === 'bottom') {
              fetchNext();
            }
          }}
          onSelectionChange={(selection) => {
            const selectedWorkspaceId = Array.from(selection)[0] as string;
            if (!isUUID(selectedWorkspaceId)) {
              return;
            }
            navigateToWorkspace(selectedWorkspaceId);
          }}
          paddingBottom={LIST_BOTTOM_PADDING}
          selectedKeys={selectedWorkspace}
          selectionBehavior="replace"
          selectionMode="single"
          variant={isGlobalNavigationFlagEnabled ? 'unstyled' : 'nav'}
        >
          {renderListItem}
        </List>
        <ContextMenu onClick={onItemSelected} />
      </ContextMenuContextProvider>
    </>
  );
};

TEXT.DEFINE({
  /** Aria label for the workspaces list */
  workspacesListAriaLabel: 'Workspaces List',
});

const LIST_BOTTOM_PADDING = 16;
